import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getClaim, getClaimAudit, getProcessingSummary, reviewClaim, rerunClaim, chatWithClaim } from '../services/api';

const STATUS_COLORS = {
  INTAKE_COMPLETE: '#38a169', INTAKE_IN_PROGRESS: '#d69e2e', VALIDATING: '#3182ce',
  UNDER_REVIEW: '#805ad5', APPROVED: '#38a169', DENIED: '#e53e3e',
  PARTIAL_APPROVED: '#dd6b20', MANUAL_REVIEW: '#d69e2e', SETTLED: '#2f855a', CLOSED: '#718096',
  HUMAN_REVIEW_PENDING: '#d69e2e', PRE_AUTH_APPROVED: '#38a169', PRE_AUTH_DENIED: '#e53e3e',
};

const SEV = {
  INFO: { bg: '#ebf8ff', color: '#2b6cb0', icon: 'ℹ️' },
  WARNING: { bg: '#fefcbf', color: '#975a16', icon: '⚠️' },
  ALERT: { bg: '#fed7d7', color: '#c53030', icon: '🚨' },
  ERROR: { bg: '#fed7d7', color: '#9b2c2c', icon: '❌' },
};

const DECISION_COLORS = {
  PASS: '#38a169', CREATED: '#3182ce', ATTACHED: '#805ad5',
  FAIL: '#e53e3e', BLOCKED: '#e53e3e', INTAKE_COMPLETE: '#38a169', FAILED: '#e53e3e',
};

export default function ClaimDetail() {
  const { claimId } = useParams();
  const [claim, setClaim] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('summary');
  const [summarySteps, setSummarySteps] = useState([]);
  const [summaryFilter, setSummaryFilter] = useState({ agent: '', status: '' });
  const [reviewComments, setReviewComments] = useState('');
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewResult, setReviewResult] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true); setError(null);
      try {
        const [claimData, auditData, summaryData] = await Promise.all([
          getClaim(claimId), getClaimAudit(claimId), getProcessingSummary(claimId)
        ]);
        setClaim(claimData);
        setSummarySteps(summaryData.steps || []);
        // Sort events: by timestamp, then by step number extracted from action
        const sorted = (auditData.events || []).sort((a, b) => {
          const tA = a.timestamp || '';
          const tB = b.timestamp || '';
          if (tA !== tB) return tA.localeCompare(tB);
          // Same timestamp — sort by step number
          const stepA = parseInt((a.action || '').match(/STEP_(\d+)/)?.[1] || '999');
          const stepB = parseInt((b.action || '').match(/STEP_(\d+)/)?.[1] || '999');
          if (stepA !== stepB) return stepA - stepB;
          // INVOKED before RESULT before COMMENT
          const order = { 'INVOKED': 0, 'RESULT': 1, 'COMMENT': 2 };
          const oA = Object.keys(order).find(k => (a.action || '').includes(k));
          const oB = Object.keys(order).find(k => (b.action || '').includes(k));
          return (order[oA] ?? 3) - (order[oB] ?? 3);
        });
        setEvents(sorted);
      } catch (err) {
        setError(err.response?.data?.detail || err.message);
      } finally { setLoading(false); }
    };
    load();
  }, [claimId]);

  if (loading) return <div style={st.container}><p style={st.loading}>Loading claim details...</p></div>;
  if (error) return <div style={st.container}><div style={st.error}>{error}</div><Link to="/claims" style={st.backLink}>← Back to Dashboard</Link></div>;
  if (!claim) return <div style={st.container}><p>Claim not found.</p></div>;

  // Categorize events
  const stepEvents = events.filter(e => e.action && e.action.includes('RESULT'));
  const commentEvents = events.filter(e => e.action && e.action.includes('COMMENT'));
  
  // Milestones in logical order
  const MILESTONE_ORDER = ['INTAKE_STARTED', 'INTAKE_COMPLETED', 'TECHNICAL_VALIDATION_STARTED', 'TECHNICAL_VALIDATION_COMPLETED', 'INTAKE_FAILED'];
  const milestoneEvents = events
    .filter(e => e.action && (e.action.includes('STARTED') || e.action.includes('COMPLETED') || e.action.includes('FAILED')))
    .filter(e => !e.action.includes('STEP_'))  // Exclude step-level events
    .filter(e => e.agentId !== 'CLAIM_INTAKE_AGENT' || !e.action.includes('CLAIM_INTAKE_COMPLETED'))  // Exclude agent's own log_audit_event
    .sort((a, b) => {
      const orderA = MILESTONE_ORDER.findIndex(m => (a.action || '').includes(m));
      const orderB = MILESTONE_ORDER.findIndex(m => (b.action || '').includes(m));
      if (orderA !== -1 && orderB !== -1) return orderA - orderB;
      if (orderA !== -1) return -1;
      if (orderB !== -1) return 1;
      return (a.timestamp || '').localeCompare(b.timestamp || '');
    });

  const fmt = (v) => v != null && v !== '' ? String(v) : '—';
  const fmtCurrency = (v) => v ? Number(v).toLocaleString('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }) : '—';

  return (
    <div style={st.container}>
      <Link to="/claims" style={st.backLink}>← Back to Dashboard</Link>

      {/* Header */}
      <div style={st.header}>
        <div>
          <h2 style={st.claimId}>{claim.claimId}</h2>
          <span style={st.claimType}>{fmt(claim.claimType)} CLAIM</span>
        </div>
        <span style={{ ...st.statusBadge, background: STATUS_COLORS[claim.status] || '#718096' }}>
          {(claim.status || '').replace(/_/g, ' ')}
        </span>
      </div>

      {/* Tabs */}
      <div style={st.tabs}>
        {['summary', 'agent_log', 'details', 'documents'].map(t => (
          <button key={t} onClick={() => setTab(t)} style={tab === t ? st.tabActive : st.tab}>
            {t === 'summary' ? '📋 Summary' : t === 'agent_log' ? '🤖 Agent Processing Log' : t === 'details' ? '📄 Full Claim Data' : '📎 Documents'}
          </button>
        ))}
      </div>

      {/* Summary Tab */}
      {tab === 'summary' && (
        <div>
          {/* Quick Info Cards */}
          <div style={st.cardGrid}>
            <InfoCard title="Patient" lines={[fmt(claim.patientName), `Age: ${fmt(claim.patientAge)} | Gender: ${fmt(claim.patientGender)}`, `ID: ${fmt(claim.patientId)}`]} />
            <InfoCard title="Hospital" lines={[fmt(claim.hospitalName || claim.hospitalId), fmt(claim.hospitalCity), `Doctor: ${fmt(claim.treatingDoctorName)}`]} />
            <InfoCard title="Diagnosis" lines={[fmt((claim.primaryDiagnosis || {}).description), `ICD-10: ${fmt((claim.primaryDiagnosis || {}).code)}`]} />
            <InfoCard title="Financial" lines={[`Claimed: ${fmtCurrency(claim.totalClaimedAmount)}`, `Sum Insured: ${fmtCurrency(claim.sumInsured)}`, `Room: ${fmt(claim.roomCategory)}`]} />
            <InfoCard title="Admission" lines={[`Date: ${fmt(claim.admissionDate)}`, `Discharge: ${fmt(claim.dischargeDate)}`, `Type: ${fmt(claim.natureOfAdmission)}`]} />
            <InfoCard title="Processing" lines={[`Status: ${fmt(claim.status)}`, `Steps: ${summarySteps.length}`, `Agents: ${[...new Set(summarySteps.map(s => s.agent_name))].length}`]} />
          </div>

          {/* Human Review Panel */}
          {['HUMAN_REVIEW_PENDING', 'MANUAL_REVIEW'].includes(claim.status) && (
            <div style={st.reviewPanel}>
              <div style={st.reviewHeader}>
                <h3 style={st.reviewTitle}>👤 Human Review Required</h3>
                <span style={{ ...st.statusBadge, background: STATUS_COLORS[claim.status] || '#718096' }}>{claim.status.replace(/_/g, ' ')}</span>
              </div>
              <div style={st.reviewAiDecision}>
                <span style={st.reviewLabel}>AI Decision:</span>
                <span style={st.reviewAiText}>{claim.statusReason || '—'}</span>
              </div>

              {reviewResult && (
                <div style={reviewResult.status?.includes('APPROVED') ? st.reviewSuccess : reviewResult.status?.includes('DENIED') ? st.reviewError : st.reviewWarning}>
                  <strong>{reviewResult.status}</strong> {reviewResult.comments && `— ${reviewResult.comments}`}
                </div>
              )}

              <textarea
                value={reviewComments}
                onChange={e => setReviewComments(e.target.value)}
                placeholder="Enter review comments (mandatory for all actions)..."
                style={st.reviewTextarea}
                disabled={reviewLoading}
              />

              <div style={st.reviewButtons}>
                <button
                  onClick={async () => {
                    if (!reviewComments.trim()) return alert('Comments are mandatory');
                    setReviewLoading(true);
                    try {
                      const res = await reviewClaim(claimId, 'APPROVE', reviewComments);
                      setReviewResult(res);
                      const newClaim = await getClaim(claimId);
                      setClaim(newClaim);
                    } catch (err) { setReviewResult({ status: 'ERROR', comments: err.response?.data?.detail || err.message }); }
                    finally { setReviewLoading(false); }
                  }}
                  disabled={reviewLoading || !reviewComments.trim()}
                  style={st.btnApprove}
                >✅ Approve</button>
                <button
                  onClick={async () => {
                    if (!reviewComments.trim()) return alert('Comments are mandatory');
                    setReviewLoading(true);
                    try {
                      const res = await reviewClaim(claimId, 'DENY', reviewComments);
                      setReviewResult(res);
                      const newClaim = await getClaim(claimId);
                      setClaim(newClaim);
                    } catch (err) { setReviewResult({ status: 'ERROR', comments: err.response?.data?.detail || err.message }); }
                    finally { setReviewLoading(false); }
                  }}
                  disabled={reviewLoading || !reviewComments.trim()}
                  style={st.btnDeny}
                >❌ Deny</button>
                <button
                  onClick={async () => {
                    if (!reviewComments.trim()) return alert('Comments are mandatory — describe what needs correction');
                    setReviewLoading(true);
                    try {
                      const res = await rerunClaim(claimId, reviewComments);
                      setReviewResult(res);
                      const [newClaim, newSummary] = await Promise.all([getClaim(claimId), getProcessingSummary(claimId)]);
                      setClaim(newClaim);
                      setSummarySteps(newSummary.steps || []);
                    } catch (err) { setReviewResult({ status: 'ERROR', comments: err.response?.data?.detail || err.message }); }
                    finally { setReviewLoading(false); }
                  }}
                  disabled={reviewLoading || !reviewComments.trim()}
                  style={st.btnRerun}
                >🔄 Rerun</button>
              </div>
              {reviewLoading && <p style={{ fontSize: 13, color: '#718096', marginTop: 8 }}>⏳ Processing...</p>}
            </div>
          )}

          {/* Processing Summary Table */}
          <h3 style={{ ...st.sectionTitle, marginTop: 28 }}>Processing Summary — Agent Execution Log</h3>

          {/* Filters */}
          <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
            <select value={summaryFilter.agent} onChange={e => setSummaryFilter(p => ({ ...p, agent: e.target.value }))} style={st.filterSelect}>
              <option value="">All Agents</option>
              {[...new Set(summarySteps.map(s => s.agent_name))].map(a => <option key={a} value={a}>{a}</option>)}
            </select>
            <select value={summaryFilter.status} onChange={e => setSummaryFilter(p => ({ ...p, status: e.target.value }))} style={st.filterSelect}>
              <option value="">All Statuses</option>
              <option value="APPROVED">APPROVED</option>
              <option value="WARNING">WARNING</option>
              <option value="DENIED">DENIED</option>
              <option value="MANUAL_REVIEW">MANUAL_REVIEW</option>
            </select>
          </div>

          {summarySteps.length === 0 ? <p style={st.empty}>No processing steps recorded yet. Submit a claim and run the pipeline.</p> : (
            <div style={{ overflowX: 'auto' }}>
              <table style={st.summaryTable}>
                <thead>
                  <tr>
                    <th style={st.summaryTh}>#</th>
                    <th style={st.summaryTh}>Agent</th>
                    <th style={st.summaryTh}>Tool</th>
                    <th style={{ ...st.summaryTh, minWidth: 250 }}>Summary</th>
                    <th style={st.summaryTh}>Decision</th>
                    <th style={st.summaryTh}>Confidence</th>
                    <th style={st.summaryTh}>Timestamp</th>
                  </tr>
                </thead>
                <tbody>
                  {summarySteps
                    .filter(s => !summaryFilter.agent || s.agent_name === summaryFilter.agent)
                    .filter(s => !summaryFilter.status || s.decision_status === summaryFilter.status)
                    .map((step, i) => {
                      const decColor = { APPROVED: '#38a169', WARNING: '#d69e2e', DENIED: '#e53e3e', MANUAL_REVIEW: '#805ad5' }[step.decision_status] || '#718096';
                      return (
                        <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#f7fafc' }}>
                          <td style={st.summaryTd}>{step.step}</td>
                          <td style={st.summaryTd}><span style={st.agentBadge}>{step.agent_name}</span></td>
                          <td style={st.summaryTd}><code style={st.toolCode}>{step.tool_name}</code></td>
                          <td style={{ ...st.summaryTd, maxWidth: 300 }}>{step.summary}</td>
                          <td style={st.summaryTd}><span style={{ ...st.decisionPill, background: decColor }}>{step.decision_status}</span></td>
                          <td style={st.summaryTd}>{step.confidence != null ? `${(step.confidence * 100).toFixed(0)}%` : '—'}</td>
                          <td style={{ ...st.summaryTd, fontSize: 11, color: '#718096' }}>{step.timestamp ? new Date(step.timestamp).toLocaleTimeString() : '—'}</td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          )}

          {/* TPA Chatbot */}
          <div style={st.chatContainer}>
            <h3 style={st.chatTitle}>💬 TPA Assistant</h3>
            <div style={st.chatMessages}>
              {chatMessages.length === 0 && (
                <div style={st.chatHint}>
                  <p style={{ margin: 0, fontWeight: 600 }}>Ask anything about this claim:</p>
                  <ul style={{ margin: '6px 0 0', paddingLeft: 20, fontSize: 12, lineHeight: 1.8 }}>
                    <li>"Why was this flagged for manual review?"</li>
                    <li>"What is the approved amount breakdown?"</li>
                    <li>"Who are the members under this policy?"</li>
                    <li>"Explain the fraud flag"</li>
                  </ul>
                </div>
              )}
              {chatMessages.map((msg, i) => (
                <div key={i} style={msg.role === 'user' ? st.chatMsgUser : st.chatMsgAssistant}>
                  <div style={st.chatMsgHeader}>{msg.role === 'user' ? '👤 You' : '🤖 AI'}</div>
                  <div style={st.chatMsgContent}>{msg.content}</div>
                  {msg.bullets && msg.bullets.length > 0 && (
                    <ul style={{ margin: '6px 0 0', paddingLeft: 16, fontSize: 12, color: '#4a5568' }}>
                      {msg.bullets.map((b, j) => <li key={j}>{b}</li>)}
                    </ul>
                  )}
                  {msg.evidence && msg.evidence.length > 0 && (
                    <div style={{ marginTop: 6, fontSize: 11, color: '#718096' }}>
                      <strong>References:</strong> {msg.evidence.join(' | ')}
                    </div>
                  )}
                  {msg.decision && <span style={{ ...st.chatDecision, background: { APPROVED: '#38a169', WARNING: '#d69e2e', DENIED: '#e53e3e', MANUAL_REVIEW: '#805ad5', HUMAN_REVIEW_PENDING: '#d69e2e' }[msg.decision] || '#718096' }}>{msg.decision}</span>}
                </div>
              ))}
              {chatLoading && <div style={st.chatMsgAssistant}><div style={st.chatMsgContent}>⏳ Thinking...</div></div>}
            </div>
            <form onSubmit={async (e) => {
              e.preventDefault();
              if (!chatInput.trim() || chatLoading) return;
              const userMsg = { role: 'user', content: chatInput };
              setChatMessages(prev => [...prev, userMsg]);
              setChatInput('');
              setChatLoading(true);
              try {
                const history = chatMessages.map(m => ({ role: m.role, content: m.content }));
                const result = await chatWithClaim(claimId, chatInput, history);
                setChatMessages(prev => [...prev, {
                  role: 'assistant',
                  content: result.answer_text || 'No response.',
                  bullets: result.summary_bullets || [],
                  evidence: result.evidence_refs || [],
                  decision: result.decision_status,
                }]);
              } catch (err) {
                setChatMessages(prev => [...prev, { role: 'assistant', content: `Error: ${err.response?.data?.detail || err.message}` }]);
              } finally { setChatLoading(false); }
            }} style={st.chatForm}>
              <input value={chatInput} onChange={e => setChatInput(e.target.value)} placeholder="Ask about this claim..." style={st.chatInput} disabled={chatLoading} />
              <button type="submit" style={st.chatBtn} disabled={chatLoading || !chatInput.trim()}>Send</button>
            </form>
          </div>
        </div>
      )}

      {/* Agent Processing Log Tab */}
      {tab === 'agent_log' && (
        <div>
          {events.length === 0 ? <p style={st.empty}>No agent processing log found.</p> : events.map((evt, i) => {
            const sev = SEV[evt.severity] || SEV.INFO;
            const isStep = evt.action && evt.action.includes('RESULT');
            const toolOutput = evt.details?.tool_output;
            return (
              <div key={evt.eventId || i} style={{ ...st.logEntry, borderLeftColor: sev.color, background: isStep ? '#fff' : '#f7fafc' }}>
                <div style={st.logHeader}>
                  <span>{sev.icon}</span>
                  <span style={{ ...st.sevLabel, color: sev.color, background: sev.bg }}>{evt.severity}</span>
                  <span style={{ fontWeight: 600, fontSize: 13, color: '#2d3748' }}>{(evt.action || '').replace(/_/g, ' ')}</span>
                  {evt.decision && <span style={{ ...st.decisionBadge, background: DECISION_COLORS[evt.decision] || '#718096' }}>{evt.decision}</span>}
                  <span style={st.timestamp}>{evt.timestamp ? new Date(evt.timestamp).toLocaleString() : ''}</span>
                </div>

                {evt.details?.tool && (
                  <div style={st.toolInfo}>
                    <span style={st.toolName}>🔧 {evt.details.tool.replace(/_/g, ' ')}</span>
                    {evt.details.tool_description && <span style={st.toolDesc}>{evt.details.tool_description}</span>}
                  </div>
                )}

                {evt.reasoning && <p style={st.reasoning}>{evt.reasoning}</p>}

                {evt.details?.summary && evt.details.summary !== evt.reasoning && (
                  <p style={{ ...st.reasoning, fontStyle: 'italic', color: '#718096' }}>{evt.details.summary}</p>
                )}

                {evt.details?.comment && (
                  <p style={{ ...st.reasoning, paddingLeft: 16, borderLeft: `2px solid ${sev.color}` }}>{evt.details.comment}</p>
                )}

                {/* Tool Output */}
                {toolOutput && (
                  <details style={st.detailsBlock}>
                    <summary style={st.detailsSummary}>Tool Response</summary>
                    <pre style={st.jsonBlock}>{JSON.stringify(toolOutput, null, 2)}</pre>
                  </details>
                )}

                {/* Full Details */}
                {evt.details && !toolOutput && !evt.details.comment && Object.keys(evt.details).length > 0 && (
                  <details style={st.detailsBlock}>
                    <summary style={st.detailsSummary}>Details</summary>
                    <pre style={st.jsonBlock}>{JSON.stringify(evt.details, null, 2)}</pre>
                  </details>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Full Claim Data Tab */}
      {tab === 'details' && (
        <div>
          <Section title="1. Patient / Insured Details">
            <Grid>
              <KV k="Patient Name" v={claim.patientName} />
              <KV k="Gender" v={claim.patientGender} />
              <KV k="Age" v={claim.patientAge} />
              <KV k="Date of Birth" v={claim.patientDob} />
              <KV k="Relationship" v={claim.patientRelationship} />
              <KV k="Policy Holder" v={claim.policyHolderName} />
              <KV k="Patient ID" v={claim.patientId} />
              <KV k="Health Card / UHID" v={claim.healthCardId} />
              <KV k="Employee ID" v={claim.employeeId} />
              <KV k="Contact" v={claim.patientContact} />
              <KV k="Email" v={claim.patientEmail} />
            </Grid>
          </Section>
          <Section title="2. Insurance Details">
            <Grid>
              <KV k="Policy Number" v={claim.policyId} />
              <KV k="Insurance Company" v={claim.insuranceCompany} />
              <KV k="TPA Name" v={claim.tpaName} />
              <KV k="Policy Type" v={claim.policyType} />
              <KV k="Policy Start" v={claim.policyStartDate} />
              <KV k="Policy End" v={claim.policyEndDate} />
              <KV k="Sum Insured" v={fmtCurrency(claim.sumInsured)} />
              <KV k="Available Balance" v={fmtCurrency(claim.availableBalance)} />
            </Grid>
          </Section>
          <Section title="3. Hospital Details">
            <Grid>
              <KV k="Hospital ID" v={claim.hospitalId} />
              <KV k="Hospital Name" v={claim.hospitalName} />
              <KV k="Address" v={claim.hospitalAddress} />
              <KV k="City" v={claim.hospitalCity} />
              <KV k="Pin Code" v={claim.hospitalPincode} />
              <KV k="Contact" v={claim.hospitalContact} />
              <KV k="Email" v={claim.hospitalEmail} />
              <KV k="Provider ID" v={claim.hospitalProviderId} />
              <KV k="Treating Doctor" v={claim.treatingDoctorName} />
              <KV k="Doctor Reg. No." v={claim.doctorRegistrationNo} />
            </Grid>
          </Section>
          <Section title="4. Admission Details">
            <Grid>
              <KV k="Nature" v={claim.natureOfAdmission} />
              <KV k="Admission Date" v={claim.admissionDate} />
              <KV k="Admission Time" v={claim.admissionTime} />
              <KV k="Discharge Date" v={claim.dischargeDate} />
              <KV k="Expected Stay (days)" v={claim.expectedDaysOfStay} />
              <KV k="Room Category" v={claim.roomCategory} />
            </Grid>
          </Section>
          <Section title="5. Diagnosis & Clinical">
            <Grid>
              <KV k="Primary Diagnosis" v={(claim.primaryDiagnosis || {}).description} />
              <KV k="ICD-10 Code" v={(claim.primaryDiagnosis || {}).code} />
              <KV k="Symptoms & Findings" v={claim.symptomsAndFindings} />
              <KV k="Past History" v={claim.pastMedicalHistory} />
              <KV k="Co-morbidities" v={claim.comorbidConditions} />
              <KV k="Duration of Illness" v={claim.durationOfIllness} />
            </Grid>
            {claim.secondaryDiagnoses && claim.secondaryDiagnoses.length > 0 && (
              <div style={{ marginTop: 8 }}>
                <strong style={{ fontSize: 13 }}>Secondary Diagnoses:</strong>
                {claim.secondaryDiagnoses.map((d, i) => <p key={i} style={{ fontSize: 13, margin: '2px 0', color: '#4a5568' }}>{d.code} — {d.description}</p>)}
              </div>
            )}
          </Section>
          <Section title="6. Treatment / Procedures">
            <Grid>
              <KV k="Line of Treatment" v={claim.lineOfTreatment} />
              <KV k="Investigation Planned" v={claim.investigationPlanned} />
              <KV k="Anesthesia Type" v={claim.anesthesiaType} />
            </Grid>
            {claim.procedures && claim.procedures.length > 0 && (
              <table style={{ ...st.miniTable, marginTop: 8 }}>
                <thead><tr><th style={st.miniTh}>Code</th><th style={st.miniTh}>Description</th><th style={st.miniTh}>Amount</th><th style={st.miniTh}>Date</th></tr></thead>
                <tbody>
                  {claim.procedures.map((p, i) => (
                    <tr key={i}><td style={st.miniTd}>{p.code}</td><td style={st.miniTd}>{p.description}</td><td style={st.miniTd}>{fmtCurrency(p.amount)}</td><td style={st.miniTd}>{fmt(p.date)}</td></tr>
                  ))}
                </tbody>
              </table>
            )}
          </Section>
          <Section title="7. Cost Breakdown">
            {claim.costBreakdown ? (
              <Grid>
                <KV k="Room Rent/day" v={fmtCurrency(claim.costBreakdown.room_rent_per_day)} />
                <KV k="Consultation" v={fmtCurrency(claim.costBreakdown.consultation_charges)} />
                <KV k="Investigation" v={fmtCurrency(claim.costBreakdown.investigation_charges)} />
                <KV k="Surgery" v={fmtCurrency(claim.costBreakdown.surgery_charges)} />
                <KV k="ICU" v={fmtCurrency(claim.costBreakdown.icu_charges)} />
                <KV k="Medicine & Consumables" v={fmtCurrency(claim.costBreakdown.medicine_consumables)} />
                <KV k="Implant / Stent" v={fmtCurrency(claim.costBreakdown.implant_cost)} />
                <KV k="Other" v={fmtCurrency(claim.costBreakdown.other_charges)} />
              </Grid>
            ) : <p style={{ fontSize: 13, color: '#a0aec0' }}>No cost breakdown provided.</p>}
            <div style={{ marginTop: 8, padding: '8px 12px', background: '#edf2f7', borderRadius: 6 }}>
              <strong>Total Claimed Amount: {fmtCurrency(claim.totalClaimedAmount)}</strong>
            </div>
          </Section>
          <Section title="8 & 9. Declarations">
            <Grid>
              <KV k="Doctor Declaration" v={claim.doctorDeclaration ? '✅ Yes' : '❌ No'} />
              <KV k="Patient Declaration" v={claim.patientDeclaration ? '✅ Yes' : '❌ No'} />
            </Grid>
          </Section>
        </div>
      )}

      {/* Documents Tab */}
      {tab === 'documents' && (
        <div>
          {(!claim.documents || claim.documents.length === 0) ? <p style={st.empty}>No documents attached.</p> : (
            <table style={st.miniTable}>
              <thead><tr><th style={st.miniTh}>File Name</th><th style={st.miniTh}>Type</th><th style={st.miniTh}>Attachment ID</th></tr></thead>
              <tbody>
                {claim.documents.map((doc, i) => (
                  <tr key={i}><td style={st.miniTd}>{doc.fileName}</td><td style={st.miniTd}>{(doc.documentType || '').replace(/_/g, ' ')}</td><td style={st.miniTd}>{doc.attachmentId}</td></tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

    </div>
  );
}

// Helper components
const InfoCard = ({ title, lines }) => (
  <div style={st.infoCard}>
    <div style={st.infoCardTitle}>{title}</div>
    {lines.map((l, i) => <div key={i} style={st.infoCardLine}>{l}</div>)}
  </div>
);

const Section = ({ title, children }) => (
  <fieldset style={st.section}><legend style={st.sectionLegend}>{title}</legend>{children}</fieldset>
);

const Grid = ({ children }) => <div style={st.kvGrid}>{children}</div>;

const KV = ({ k, v }) => (
  <div style={st.kvItem}><span style={st.kvKey}>{k}</span><span style={st.kvVal}>{v != null && v !== '' && v !== '—' ? String(v) : '—'}</span></div>
);

const st = {
  container: { maxWidth: 1100, margin: '0 auto', padding: 24, fontFamily: 'system-ui, sans-serif' },
  backLink: { color: '#3182ce', textDecoration: 'none', fontSize: 14, display: 'inline-block', marginBottom: 16 },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, padding: '16px 20px', background: '#fff', borderRadius: 10, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' },
  claimId: { margin: 0, color: '#1a365d', fontSize: 22 },
  claimType: { fontSize: 12, color: '#718096', fontWeight: 600, letterSpacing: 1 },
  statusBadge: { padding: '6px 18px', borderRadius: 20, color: '#fff', fontSize: 13, fontWeight: 700 },
  tabs: { display: 'flex', gap: 4, marginBottom: 20, borderBottom: '2px solid #e2e8f0', paddingBottom: 0 },
  tab: { padding: '10px 20px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, color: '#718096', borderBottom: '2px solid transparent', marginBottom: -2 },
  tabActive: { padding: '10px 20px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, color: '#2b6cb0', fontWeight: 600, borderBottom: '2px solid #2b6cb0', marginBottom: -2 },
  cardGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 12, marginBottom: 24 },
  infoCard: { background: '#fff', borderRadius: 8, padding: '14px 16px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' },
  infoCardTitle: { fontSize: 11, fontWeight: 700, color: '#a0aec0', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 },
  infoCardLine: { fontSize: 13, color: '#2d3748', lineHeight: 1.6 },
  sectionTitle: { fontSize: 16, color: '#1a365d', marginBottom: 12, marginTop: 8 },
  timeline: { marginBottom: 24 },
  timelineItem: { padding: '10px 16px', marginBottom: 6, borderLeft: '4px solid #cbd5e0', background: '#fff', borderRadius: '0 6px 6px 0' },
  timelineHeader: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  timestamp: { marginLeft: 'auto', fontSize: 11, color: '#a0aec0' },
  reasoning: { fontSize: 13, color: '#4a5568', lineHeight: 1.6, margin: '6px 0 0' },
  decisionBadge: { padding: '2px 8px', borderRadius: 4, color: '#fff', fontSize: 11, fontWeight: 700 },
  remarkCard: { padding: '10px 14px', marginBottom: 6, borderLeft: '4px solid #cbd5e0', borderRadius: '0 6px 6px 0' },
  sevLabel: { fontSize: 11, fontWeight: 700, padding: '1px 6px', borderRadius: 3 },
  remarkTool: { fontSize: 11, color: '#718096', marginLeft: 8, textTransform: 'uppercase' },
  remarkText: { fontSize: 13, color: '#2d3748', margin: '4px 0 0', lineHeight: 1.5 },
  logEntry: { padding: '12px 16px', marginBottom: 8, borderLeft: '4px solid #cbd5e0', borderRadius: '0 6px 6px 0', boxShadow: '0 1px 2px rgba(0,0,0,0.04)' },
  logHeader: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  toolInfo: { display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 },
  toolName: { fontSize: 13, fontWeight: 600, color: '#2b6cb0' },
  toolDesc: { fontSize: 12, color: '#718096' },
  detailsBlock: { marginTop: 8 },
  detailsSummary: { fontSize: 12, color: '#3182ce', cursor: 'pointer', fontWeight: 600 },
  jsonBlock: { background: '#f7fafc', padding: 10, borderRadius: 6, fontSize: 11, overflow: 'auto', maxHeight: 250, marginTop: 4 },
  section: { border: '1px solid #e2e8f0', borderRadius: 8, padding: 16, marginBottom: 12 },
  sectionLegend: { fontWeight: 600, color: '#2d3748', padding: '0 8px', fontSize: 14 },
  kvGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '6px 16px' },
  kvItem: { display: 'flex', flexDirection: 'column', padding: '4px 0' },
  kvKey: { fontSize: 11, color: '#a0aec0', fontWeight: 600, textTransform: 'uppercase' },
  kvVal: { fontSize: 14, color: '#2d3748' },
  miniTable: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  miniTh: { textAlign: 'left', padding: '8px 12px', background: '#edf2f7', color: '#2d3748', fontSize: 12, fontWeight: 600 },
  miniTd: { padding: '6px 12px', borderBottom: '1px solid #edf2f7', color: '#4a5568' },
  loading: { textAlign: 'center', color: '#718096', padding: 40 },
  error: { background: '#fed7d7', color: '#c53030', padding: 12, borderRadius: 8, marginBottom: 16 },
  empty: { textAlign: 'center', color: '#a0aec0', padding: 40 },
  summaryTable: { width: '100%', borderCollapse: 'collapse', fontSize: 13, border: '1px solid #e2e8f0' },
  summaryTh: { textAlign: 'left', padding: '10px 12px', background: '#1a365d', color: '#fff', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', position: 'sticky', top: 0 },
  summaryTd: { padding: '8px 12px', borderBottom: '1px solid #edf2f7', verticalAlign: 'top', lineHeight: 1.5 },
  agentBadge: { display: 'inline-block', padding: '2px 8px', background: '#ebf8ff', color: '#2b6cb0', borderRadius: 4, fontSize: 11, fontWeight: 600 },
  toolCode: { background: '#edf2f7', padding: '2px 6px', borderRadius: 3, fontSize: 11, color: '#4a5568' },
  decisionPill: { display: 'inline-block', padding: '3px 10px', borderRadius: 12, color: '#fff', fontSize: 11, fontWeight: 700 },
  filterSelect: { padding: '6px 12px', border: '1px solid #cbd5e0', borderRadius: 6, fontSize: 13 },
  reviewPanel: { marginTop: 20, marginBottom: 20, border: '1px solid #e2e8f0', borderRadius: 12, padding: 20, background: '#fff', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', borderLeft: '4px solid #d69e2e' },
  reviewHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 },
  reviewTitle: { margin: 0, fontSize: 16, color: '#744210' },
  reviewAiDecision: { display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 12, padding: '10px 14px', background: '#f7fafc', borderRadius: 8 },
  reviewLabel: { fontSize: 12, fontWeight: 700, color: '#744210', minWidth: 90, flexShrink: 0 },
  reviewAiText: { fontSize: 13, color: '#4a5568', lineHeight: 1.5 },
  reviewInfo: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 },
  reviewTextarea: { width: '100%', minHeight: 70, padding: '10px 14px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, resize: 'vertical', marginBottom: 12, boxSizing: 'border-box', fontFamily: 'system-ui, sans-serif' },
  reviewButtons: { display: 'flex', gap: 10 },
  btnApprove: { padding: '9px 20px', background: '#38a169', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  btnDeny: { padding: '9px 20px', background: '#e53e3e', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  btnRerun: { padding: '9px 20px', background: '#3182ce', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  reviewSuccess: { background: '#c6f6d5', color: '#276749', padding: 10, borderRadius: 6, marginBottom: 12, fontSize: 13 },
  reviewError: { background: '#fed7d7', color: '#c53030', padding: 10, borderRadius: 6, marginBottom: 12, fontSize: 13 },
  reviewWarning: { background: '#fefcbf', color: '#975a16', padding: 10, borderRadius: 6, marginBottom: 12, fontSize: 13 },
  chatContainer: { marginTop: 20, marginBottom: 20, border: '1px solid #e2e8f0', borderRadius: 12, padding: 16, background: '#fff', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', borderLeft: '4px solid #2b6cb0' },
  chatTitle: { margin: '0 0 12px', fontSize: 16, color: '#1a365d' },
  chatMessages: { maxHeight: 350, overflowY: 'auto', marginBottom: 12, padding: '8px 0' },
  chatHint: { color: '#718096', fontSize: 13, padding: '12px 16px', background: '#f7fafc', borderRadius: 8 },
  chatMsgUser: { padding: '8px 14px', marginBottom: 8, background: '#ebf8ff', borderRadius: '12px 12px 4px 12px', maxWidth: '80%', marginLeft: 'auto' },
  chatMsgAssistant: { padding: '8px 14px', marginBottom: 8, background: '#f7fafc', borderRadius: '12px 12px 12px 4px', maxWidth: '85%', border: '1px solid #e2e8f0' },
  chatMsgHeader: { fontSize: 11, fontWeight: 700, color: '#a0aec0', marginBottom: 4 },
  chatMsgContent: { fontSize: 13, color: '#2d3748', lineHeight: 1.6, whiteSpace: 'pre-wrap' },
  chatDecision: { display: 'inline-block', marginTop: 6, padding: '2px 8px', borderRadius: 4, fontSize: 11, color: '#fff', fontWeight: 700 },
  chatForm: { display: 'flex', gap: 8 },
  chatInput: { flex: 1, padding: '9px 14px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13 },
  chatBtn: { padding: '9px 18px', background: '#2b6cb0', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' },
};
